'use client';

import { CSSProperties, FC, Ref, useEffect, useRef, useState } from 'react';
import { useSurfsideContext } from '@surfside/ads-react';
import classNames from 'classnames';

import { getSlicePadding } from '@/utils';
import { useIsServerSide, useSelectedStore } from '@/hooks';
import { Container } from '@/components';
import { AdsSlice } from '@/types';

import { SurfsideBanner } from './SurfBanner';
import { SurfsideVideo } from './SurfVideo';
import AdsCarousel from './AdsCarousel';
import { getRecommenderType } from './utils';

import styles from './ads.module.scss';

/*
 *** DOC ***
 *** https://docs.surfside.io/commerce-media/enabling-ads ***
 */

const Ads: FC<AdsSlice> = slice => {
  const { primary, variation } = slice;
  const { store } = useSelectedStore();
  const { isServerSide } = useIsServerSide();
  const { isReady, startRequest } = useSurfsideContext();
  const [adsLocationId, setAdsLocationId] = useState<string | null>(null);
  const startRequestRef = useRef<typeof startRequest>(undefined);
  const contentRef = useRef<HTMLElement>(null);
  const storeLocationId = store?.short_name;

  if (adsLocationId && adsLocationId !== storeLocationId) {
    setAdsLocationId(null);
  }

  useEffect(() => {
    if (!isReady || !startRequest || !storeLocationId) return;
    if (startRequestRef.current === startRequest) return;

    startRequestRef.current = startRequest;
    setAdsLocationId(storeLocationId);
  }, [isReady, startRequest, storeLocationId]);

  const paddingSection = primary?.padding_section;
  const marginBottom = primary?.bottom_spacing;
  const background = primary?.section_background_color ?? '';
  const backgroundImage = primary?.background_image?.url;
  const backgroundStyleFixed = primary?.background_style_fixed_cover;
  const minHeight = primary?.min_heigth;
  const fullWidth =
    (variation === 'default' || variation === 'video') &&
    'full_width' in primary &&
    Boolean(primary.full_width);
  const accountId = process?.env?.NEXT_PUBLIC_SURFSIDE_ACCOUNT_ID ?? '';
  const channelId = process?.env?.NEXT_PUBLIC_SURFSIDE_CHANNEL_ID ?? '';
  const siteId = process?.env?.NEXT_PUBLIC_SURFSIDE_SOURCE_ID ?? '';

  if (!primary?.active || isServerSide || !store?.short_name) return null;

  // Carousel owns its <section> so error/empty hides the whole slice
  if (slice.variation === 'productCarousel') {
    const { primary: carouselPrimary } = slice;

    if (adsLocationId !== store.short_name) return null;

    return (
      <AdsCarousel
        activeControlColor={carouselPrimary.active_control_color}
        analyticsListName="ads-carousel"
        backgroundColor={carouselPrimary.section_background_color}
        backgroundImage={carouselPrimary.background_image}
        backgroundStyleFixed={carouselPrimary.background_style_fixed_cover}
        category={carouselPrimary.category}
        disableControlColor={carouselPrimary.disable_control_color}
        loadMoreText={carouselPrimary.load_more_text}
        loadMoreTextColor={carouselPrimary.load_more_text_color}
        locationId={store.short_name}
        maxProducts={
          (carouselPrimary.max_items ?? 6) * (carouselPrimary.max_page ?? 1)
        }
        minHeight={carouselPrimary.min_heigth}
        paddingSection={carouselPrimary.padding_section}
        pageSize={carouselPrimary.max_items ?? 6}
        recommenderType={getRecommenderType(carouselPrimary.recommend)}
        spacingBottom={carouselPrimary.bottom_spacing}
        strategy={carouselPrimary.strategy ?? 'sponsored'}
        title={carouselPrimary.title ?? ''}
        titleColor={carouselPrimary.title_color}
        zoneId={carouselPrimary.zone_id!}
      />
    );
  }

  const renderAd = () => {
    switch (slice.variation) {
      case 'video': {
        const { primary: videoPrimary } = slice;
        return (
          <SurfsideVideo
            accountId={accountId}
            channelId={channelId}
            height={videoPrimary.height!}
            locationId={store?.short_name ?? ''}
            maxDuration={videoPrimary.max_duration as number}
            minDuration={videoPrimary.min_duration ?? undefined}
            siteId={siteId}
            style={{ textAlign: 'center' }}
            width={videoPrimary.width!}
            zoneId={videoPrimary.zone_id!}
          />
        );
      }
      default: {
        const { primary: bannerPrimary } = slice;
        return (
          <SurfsideBanner
            accountId={accountId}
            channelId={channelId}
            height={bannerPrimary.height!}
            locationId={store?.short_name ?? ''}
            siteId={siteId}
            style={{ textAlign: 'center' }}
            width={bannerPrimary.width!}
            zoneId={bannerPrimary.zone_id as string}
          />
        );
      }
    }
  };

  const sectionStyle: CSSProperties = {
    background,
    backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
    minHeight: `${minHeight || 0}vh`,
  };

  return (
    <section
      aria-label="surfside ads"
      className={classNames(
        styles.section__ads,
        getSlicePadding(paddingSection),
        {
          ['spacing_bottom']: marginBottom,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={sectionStyle}
    >
      <Container
        className={classNames({
          [styles.container_full_width]: fullWidth,
        })}
        key={store?.short_name}
        ref={contentRef as Ref<HTMLDivElement> | undefined}
      >
        {renderAd()}
      </Container>
    </section>
  );
};

export default Ads;
